
import { defineComponent, onBeforeMount, reactive } from 'vue'

// components
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'

// classes
import { DataState } from '@/core/classes/DataState.class'

// types
import { CompanyT } from '@/core/types/companies.types'

// realisations
import { companies } from '@/core/realisations/companies.realisation'

// constants
import { STATUSES } from '@/core/constants/orders.constants'
import { CityT } from '@/core/types/cities.types'
import { cities } from '@/core/realisations/cities.realisation'
import { ManagerT, UserT } from '@/core/types/users.types'
import { users } from '@/core/realisations/users.realisation'

// validations
import useVuelidate from '@vuelidate/core'
import { integer } from '@vuelidate/validators'
import { CarrierT } from '@/core/types/carriers.types'
import { carriers } from '@/core/realisations/carriers.realisation'
import { CouirerCarT } from '@/core/types/couriers.types'
import { couriers } from '@/core/realisations/couriers.realisation'
import { useDate } from '@/common/composable/useDate'
import { FtlGetListParamsCamel } from '@/core/types/ftl_bids.types'

const FTL_TYPES = [
    {
        id: 0,
        name: 'Междугородняя перевозка',
    },
    {
        id: 1,
        name: 'Международная перевозка',
    },
    {
        id: 2,
        name: 'Внутригородская перевозка',
    },
]

const WITHOUT_AUTO_OPTIONS = [
    { id: 1, name: 'Да', value: '0' },
    { id: 2, name: 'Нет', value: '1' },
]

export default defineComponent({
    components: {
        TheInput,
        TheSelect,
        TheButton,
        Checkbox,
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['resetted', 'search'],
    setup(props, { emit }) {
        const { getToday, getYesterday, getThisWeek, getPreviousWeek, getThisMonth } = useDate()

        const state = reactive({
            id: null as string | null,
            dop_invoice_number: null as string | null,
            created_at_from: null as string | null,
            created_at_to: null as string | null,
            from_act_date: null as string | null,
            to_act_date: null as string | null,
            company_id: null as number | null,
            act_number: null as string | null,
            city_from_id: null as number | null,
            city_to_id: null as number | null,
            car_id: null as number | null,
            carrier_id: null as number | null,
            manager_id: null as number | null,
            dispatcher_id: null as number | null,
            logist_id: null as number | null,
            is_need_car: null as string | null,
            take_date: null as string | null,
            //
            status: [] as number[],
            ftl_types: [] as number[],
        })

        const state_checkbox = reactive({
            is_today: false,
            is_yesterday: false,
            is_this_week: false,
            is_previous_week: false,
            is_this_month: false,
        })

        const state_form$ = useVuelidate(
            {
                id: { integer },
            },
            state
        )

        const data_state = reactive({
            companies: new DataState<CompanyT>({}),
            cities: new DataState<CityT>({}),
            managers: new DataState<ManagerT>({}),
            logists: new DataState<UserT>({}),
            carriers: new DataState<CarrierT>({}),
            cars: new DataState<CouirerCarT>({}),
            dispatchers: new DataState<UserT>({}),
        })

        const resetCheckboxes = (type?: string) => {
            if (type !== 'today') state_checkbox.is_today = false
            if (type !== 'yesterday') state_checkbox.is_yesterday = false
            if (type !== 'this_week') state_checkbox.is_this_week = false
            if (type !== 'previous_week') state_checkbox.is_previous_week = false
            if (type !== 'this_month') state_checkbox.is_this_month = false
        }

        const reset = () => {
            if (props.loading) return

            state.id = null
            state.dop_invoice_number = null
            state.status = []
            state.carrier_id = null
            state.car_id = null
            state.ftl_types = []
            state.company_id = null
            state.created_at_from = null
            state.created_at_to = null
            state.from_act_date = null
            state.to_act_date = null
            state.take_date = null
            state.act_number = null
            state.city_from_id = null
            state.city_to_id = null
            state.manager_id = null
            state.logist_id = null
            state.dispatcher_id = null
            state.is_need_car = null

            resetCheckboxes()

            emit('resetted')
        }

        const search = () => {
            if (props.loading) return

            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            let is_need_car = null as number | null

            if (state.is_need_car === '0') {
                is_need_car = 0
            }

            if (state.is_need_car === '1') {
                is_need_car = 1
            }

            emit('search', {
                id: state.id,
                dopOrderNumber: state.dop_invoice_number,
                startTime: state.created_at_from,
                endTime: state.created_at_to,
                fromActDate: state.from_act_date,
                toActDate: state.to_act_date,
                companyId: state.company_id,
                cityFromId: state.city_from_id,
                cityToId: state.city_to_id,
                carId: state.car_id,
                carrierId: state.carrier_id,
                managerId: state.manager_id,
                logistId: state.logist_id,
                dispatcherId: state.dispatcher_id,
                isNeedCar: is_need_car,
                actNumber: state.act_number,
                statusIds: state.status,
                ftlTypes: state.ftl_types,
                takeDate: state.take_date,
            } as FtlGetListParamsCamel)
        }

        const manageToday = () => {
            if (state_checkbox.is_today) {
                state.created_at_from = getToday()
                state.created_at_to = getToday()
            } else {
                state.created_at_from = null
                state.created_at_to = null
            }
        }

        const manageYesterday = () => {
            if (state_checkbox.is_yesterday) {
                state.created_at_from = getYesterday()
                state.created_at_to = getYesterday()
            } else {
                state.created_at_from = null
                state.created_at_to = null
            }
        }

        const manageThisWeek = () => {
            if (state_checkbox.is_this_week) {
                const { monday, sunday } = getThisWeek()

                state.created_at_from = monday
                state.created_at_to = sunday
            } else {
                state.created_at_from = null
                state.created_at_to = null
            }
        }

        const managePreviousWeek = () => {
            if (state_checkbox.is_previous_week) {
                const { previousMonday, previousSunday } = getPreviousWeek()

                state.created_at_from = previousMonday
                state.created_at_to = previousSunday
            } else {
                state.created_at_from = null
                state.created_at_to = null
            }
        }

        const manageThisMonth = () => {
            if (state_checkbox.is_this_month) {
                const { firstDay, lastDay } = getThisMonth()

                state.created_at_from = firstDay
                state.created_at_to = lastDay
            } else {
                state.created_at_from = null
                state.created_at_to = null
            }
        }

        const onCheckboxChange = (type: string) => {
            resetCheckboxes(type)

            if (type === 'today') manageToday()
            else if (type === 'yesterday') manageYesterday()
            else if (type === 'this_week') manageThisWeek()
            else if (type === 'previous_week') managePreviousWeek()
            else if (type === 'this_month') manageThisMonth()
        }

        const loadCompanies = async (page: number, search?: string) => {
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true
                const response = await companies.getList({
                    page,
                    search,
                    isExtended: 0,
                    statusId: 2,
                })
                data_state.companies.list = response.data

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const loadMoreCompanies = async (page: number) => {
            if (page > data_state.companies.pagination.total_pages) return
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true

                const response = await companies.getList({ page, isExtended: 0, statusId: 2 })
                data_state.companies.list = data_state.companies.list.concat(response.data)

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const searchCompanies = (search_query: string) => {
            loadCompanies(1, search_query)
        }

        const unshiftFindedCompany = async (company: CompanyT) => {
            await loadCompanies(data_state.companies.pagination.current_page)
            data_state.companies.list.unshift(company)
        }

        // CITIES
        const loadCities = async (page: number, search?: string) => {
            data_state.cities.pagination.current_page = page

            try {
                data_state.cities.is_loading = true

                const data = await cities.getAllList(page, search)
                data_state.cities.list = data.data

                data_state.cities.pagination.total_pages = data.meta.last_page

                data_state.cities.is_failed = false
                data_state.cities.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.cities.failure.catchError(error)

                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_failed = true
                }
            } finally {
                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_loading = false
                }
            }
        }

        const loadMoreCities = async (page: number) => {
            if (page > data_state.cities.pagination.total_pages) return
            data_state.cities.pagination.current_page = page

            try {
                data_state.cities.is_loading = true

                const response = await cities.getAllList(page)
                data_state.cities.list = data_state.cities.list.concat(response.data)

                data_state.cities.pagination.total_pages = response.meta.last_page

                data_state.cities.is_failed = false
                data_state.cities.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.cities.failure.catchError(error)

                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_failed = true
                }
            } finally {
                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_loading = false
                }
            }
        }

        const searchCity = (search_query: string) => {
            // data_state.cities.pagination.current_page
            loadCities(1, search_query)
        }

        const unshiftFindedCity = async (city: CityT) => {
            await loadCities(data_state.cities.pagination.current_page)
            data_state.cities.list.unshift(city)
        }

        // MANAGERS
        const loadManagers = async (page: number, search?: string) => {
            data_state.managers.pagination.current_page = page

            try {
                data_state.managers.is_loading = true

                const response = await users.getManagers({
                    page,
                    name: search,
                    statusId: 1,
                    isExtended: 0,
                })

                data_state.managers.list = response.data
                data_state.managers.pagination.total_pages = response.meta.last_page

                data_state.managers.is_failed = false
                data_state.managers.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.managers.failure.catchError(error)

                if (!data_state.managers.failure.error_state.is_cancel) {
                    data_state.managers.is_failed = true
                }
            } finally {
                if (!data_state.managers.failure.error_state.is_cancel) {
                    data_state.managers.is_loading = false
                }
            }
        }

        const loadMoreManagers = async (page: number) => {
            if (page > data_state.managers.pagination.total_pages) return
            data_state.managers.pagination.current_page = page

            try {
                data_state.managers.is_loading = true

                const response = await users.getManagers({ page, statusId: 1, isExtended: 0 })
                data_state.managers.list = data_state.managers.list.concat(response.data)

                data_state.managers.pagination.total_pages = response.meta.last_page

                data_state.managers.is_failed = false
                data_state.managers.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.managers.failure.catchError(error)

                if (!data_state.managers.failure.error_state.is_cancel) {
                    data_state.managers.is_failed = true
                }
            } finally {
                if (!data_state.managers.failure.error_state.is_cancel) {
                    data_state.managers.is_loading = false
                }
            }
        }

        const searchManagers = async (search_query: string) => {
            await loadManagers(1, search_query)
        }

        const unshiftFoundManager = async (manager: ManagerT) => {
            await loadManagers(data_state.managers.pagination.current_page)
            data_state.managers.list.unshift(manager)
        }

        const loadDispatchers = async () => {
            try {
                data_state.dispatchers.is_loading = true

                const params = {
                    page: 1,
                    limit: 9999,
                    roleId: 16,
                }
                const data = await users.getList(params)

                data_state.dispatchers.list = data.data

                data_state.dispatchers.is_failed = false
            } catch (error) {
                console.error(error)
                data_state.dispatchers.is_failed = true
            } finally {
                data_state.dispatchers.is_loading = false
            }
        }

        const loadLogists = async () => {
            try {
                data_state.logists.is_loading = true

                const params = {
                    page: 1,
                    limit: 9999,
                    roleId: 21,
                }
                const data = await users.getList(params)

                data_state.logists.list = data.data

                data_state.logists.is_failed = false
            } catch (error) {
                console.error(error)
                data_state.logists.is_failed = true
            } finally {
                data_state.logists.is_loading = false
            }
        }

        const loadCarriers = async () => {
            data_state.carriers.pagination.current_page = 1

            try {
                const params = { page: 1, getAllCarriers: 1, limit: 9999, statusId: 2 }

                data_state.carriers.is_loading = true
                const data = await carriers.getList(params)

                data_state.carriers.list = data.data
                data_state.carriers.pagination.total_pages = data.meta.last_page

                data_state.carriers.is_failed = false
                data_state.carriers.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.carriers.failure.catchError(error)

                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_failed = true
                }
            } finally {
                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_loading = false
                }
            }
        }

        const loadCars = async () => {
            data_state.cars.pagination.current_page = 1

            try {
                const params = { page: 1, limit: 9999 }

                data_state.cars.is_loading = true
                const data = await couriers.getCars(params)

                data_state.cars.list = data.data
                data_state.cars.pagination.total_pages = data.meta.last_page

                data_state.cars.is_failed = false
                data_state.cars.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.cars.failure.catchError(error)

                if (!data_state.cars.failure.error_state.is_cancel) {
                    data_state.cars.is_failed = true
                }
            } finally {
                if (!data_state.cars.failure.error_state.is_cancel) {
                    data_state.cars.is_loading = false
                }
            }
        }

        //

        onBeforeMount(() => {
            loadCompanies(1)
            loadCities(1)
            loadManagers(1)
            loadCarriers()
            loadCars()
            loadDispatchers()
            loadLogists()
        })

        return {
            WITHOUT_AUTO_OPTIONS,
            STATUSES,
            FTL_TYPES,
            state,
            state_checkbox,
            data_state,
            reset,
            search,
            loadCompanies,
            loadMoreCompanies,
            searchCompanies,
            unshiftFindedCompany,
            // cities
            loadCities,
            loadMoreCities,
            searchCity,
            unshiftFindedCity,
            // managers
            loadManagers,
            loadMoreManagers,
            searchManagers,
            unshiftFoundManager,
            loadDispatchers,
            loadLogists,
            state_form$,
            loadCarriers,
            loadCars,
            onCheckboxChange,
        }
    },
})
